import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { EditLink } from "../../redux/actions/EditLinkAction";

const EditLinkModal = ({ levels1, levels2, open, onClose, linkData }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.editLink.status);
  const [link, setLink] = useState({
    idmenuLevel3Links: "",
    name: "",
    url: "",
    isActive: true,
    createdBy: "",
    createdDate: "",
    modifiedBy: "",
    modifiedDate: "",
    idmenuLevel1: "",
    idmenuLevel2: "",
    idcountry: "",
  });
  const [message, setMessage] = useState({ type: "", text: "" });
  const loading = status === "loading";

  useEffect(() => {
    if (linkData && open) {
      setLink({
        idmenuLevel3Links: linkData.id ?? "",
        name: linkData.linkName || "",
        url: linkData.url || "",
        isActive: linkData.isActive ?? true,
        createdBy: linkData.createdBy || "",
        createdDate: linkData.createdDate || "",
        modifiedBy: linkData.modifiedBy || "",
        modifiedDate: linkData.modifiedDate || "",
        idmenuLevel1: linkData.level1Id || "",
        idmenuLevel2: linkData.level2Id || "",
        idcountry: linkData.idCountry || "",
      });
      setMessage({ type: "", text: "" });
    }
  }, [linkData, open]);

  const isFormValid = () =>
    link.idmenuLevel3Links !== "" &&
    link.name.trim() !== "" &&
    link.url.trim() !== "" &&
    link.idmenuLevel1 !== "" &&
    link.idmenuLevel2 !== "";

  const handleClose = () => {
    onClose();
  };

  const getLevel2Options = () => {
    if (!link.idmenuLevel1) {
      return [];
    }

    return levels2;
  };

  const handleSaveLink = async () => {
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(EditLink(link));

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error updating the link. Please try again.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Link</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="ID"
            value={link.idmenuLevel3Links}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Link Name"
            value={link.name}
            onChange={(e) => setLink({ ...link, name: e.target.value })}
            fullWidth
            required
            disabled={loading}
          />
          <TextField
            label="URL"
            value={link.url}
            onChange={(e) => setLink({ ...link, url: e.target.value })}
            fullWidth
            required
            disabled={loading}
            placeholder="https://example.com"
          />
          <FormControlLabel
            control={
              <Switch
                checked={link.isActive}
                onChange={(e) =>
                  setLink({ ...link, isActive: e.target.checked })
                }
                disabled={loading}
              />
            }
            label="Active"
          />
          <TextField
            select
            label="Section (Level 1)"
            value={link.idmenuLevel1}
            onChange={(e) =>
              setLink({
                ...link,
                idmenuLevel1: e.target.value,
                idmenuLevel2: "",
              })
            }
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "42px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1.2rem",
              },
            }}
          >
            {levels1.map((option) => (
              <MenuItem key={option.idmenuLevel1} value={option.idmenuLevel1}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Category (Level 2)"
            value={link.idmenuLevel2}
            onChange={(e) => setLink({ ...link, idmenuLevel2: e.target.value })}
            fullWidth
            required
            disabled={!link.idmenuLevel1 || loading}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "42px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1.2rem",
              },
            }}
          >
            {getLevel2Options().map((option) => (
              <MenuItem key={option.idmenuLevel2} value={option.idmenuLevel2}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveLink}
          disabled={!isFormValid() || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{
            backgroundColor: "#4b4bfa",
            "&:hover": {
              backgroundColor: "#3e3ed9",
            },
            color: "#fff",
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLinkModal;
